import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Select, Tree, Spin, Empty, Alert } from "antd";
import { InfoCircleOutlined, ApartmentOutlined } from "@ant-design/icons";
import type { DataNode } from "antd/es/tree";

import { ContentCard, useBoards } from "../../component/admin";
import { getBoardHeaderInfo } from "../../component/admin/config/boardHeaderStructure";
import { getCategoryInfo } from "../../api/categoryAPI";
import CategoryPermissionWidget from "../../component/category/CategoryPermissionWidget";

interface PermBoard {
  boardId: number;
  name: string;
  label: string;
  rootCategoryId: number;
}

interface SelectedCategory {
  id: number;
  name: string;
}

const BoardCategoryPermissionPage = () => {
  const { boards, loading: boardsLoading } = useBoards();

  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory | null>(null);

  // 카테고리별 권한이 켜진 게시판만
  const permBoards: PermBoard[] = useMemo(
    () =>
      (boards ?? [])
        .filter(
          (b: any) =>
            b.isPermissionEnabled &&
            b.isCategoryPermissionEnabled &&
            b.rootCategories?.[0]?.id != null
        )
        .map((b: any) => ({
          boardId: b.board_id,
          name: b.name,
          label: getBoardHeaderInfo(b.name).label,
          rootCategoryId: b.rootCategories[0].id as number,
        }))
        .sort(
          (a: PermBoard, b: PermBoard) =>
            getBoardHeaderInfo(a.name).order - getBoardHeaderInfo(b.name).order
        ),
    [boards]
  );

  const selectedBoard = useMemo(
    () => permBoards.find((b) => b.boardId === selectedBoardId) ?? null,
    [permBoards, selectedBoardId]
  );

  // 게시판 목록 로드되면 첫 게시판 자동 선택
  useEffect(() => {
    if (selectedBoardId == null && permBoards.length > 0) {
      setSelectedBoardId(permBoards[0].boardId);
    }
  }, [permBoards, selectedBoardId]);

  // 선택 게시판의 카테고리 트리 로드
  useEffect(() => {
    if (!selectedBoard) {
      setTreeData([]);
      setSelectedCategory(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setTreeLoading(true);
      setSelectedCategory(null);
      try {
        const root = await getCategoryInfo(selectedBoard.rootCategoryId);
        if (cancelled) return;
        const mains = [...(root.children ?? [])].sort(
          (a: any, b: any) => a.listOrder - b.listOrder
        );
        const nodes: DataNode[] = mains.map((main: any) => {
          const subs = [...(main.children ?? [])].sort(
            (a: any, b: any) => a.listOrder - b.listOrder
          );
          return {
            key: main.id,
            title: main.name,
            children: subs.map((sub: any) => ({
              key: sub.id,
              title: <SubNodeLabel>{sub.name}</SubNodeLabel>,
              isLeaf: true,
              // 서브 카테고리는 표시만 하고 직접 권한 관리는 하지 않는다.
              selectable: false,
            })),
          };
        });
        setTreeData(nodes);
      } catch (e) {
        console.error("카테고리 트리 로드 실패:", e);
        setTreeData([]);
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBoard]);

  const handleTreeSelect = (_: React.Key[], info: any) => {
    const node = info?.node;
    if (!node) return;
    setSelectedCategory({ id: Number(node.key), name: String(node.title) });
  };

  return (
    <ContentCard title="게시판-카테고리 권한 관리">
      <InfoBox>
        <Alert
          message="게시판과 카테고리를 선택해 해당 카테고리의 권한 보유자를 관리할 수 있습니다."
          description={
            <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
              <li>
                카테고리별 권한이 켜진 게시판만 목록에 표시됩니다.
              </li>
              <li>
                왼쪽 트리에서 <strong>대분류 카테고리</strong>를 선택하면,
                오른쪽에서 해당 카테고리의 <strong>권한 보유자</strong>를 확인하고 추가/제거할 수 있습니다.
              </li>
              <li style={{ color: "#8c8c8c" }}>
                소분류 카테고리는 표시만 되며, 직접 권한 관리는 대분류 단위로 이루어집니다.
              </li>
            </ul>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          closable
        />
      </InfoBox>

      <TopBar>
        <label>게시판</label>
        <Select
          style={{ minWidth: 280 }}
          placeholder="게시판 선택"
          loading={boardsLoading}
          value={selectedBoardId ?? undefined}
          onChange={(v) => setSelectedBoardId(v)}
          options={permBoards.map((b) => ({ value: b.boardId, label: b.label }))}
          notFoundContent={
            boardsLoading ? "불러오는 중..." : "카테고리별 권한 게시판이 없습니다."
          }
        />
      </TopBar>

      <Body>
        <TreePanel>
          <PanelTitle>
            <ApartmentOutlined /> 카테고리
          </PanelTitle>
          {treeLoading ? (
            <Center>
              <Spin />
            </Center>
          ) : treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={selectedBoard ? "카테고리가 없습니다." : "게시판을 선택하세요."}
              style={{ marginTop: 40 }}
            />
          ) : (
            <StyledTree
              treeData={treeData}
              defaultExpandAll
              blockNode
              selectedKeys={selectedCategory ? [selectedCategory.id] : []}
              onSelect={handleTreeSelect}
            />
          )}
        </TreePanel>

        <WidgetArea>
          {selectedCategory ? (
            <CategoryPermissionWidget
              key={selectedCategory.id}
              categoryId={selectedCategory.id}
              categoryName={selectedCategory.name}
              columns={3}
            />
          ) : (
            <PlaceHolder>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="카테고리를 선택하면 권한 보유자가 표시됩니다."
              />
            </PlaceHolder>
          )}
        </WidgetArea>
      </Body>
    </ContentCard>
  );
};

export default BoardCategoryPermissionPage;

/* ============================ styled ============================ */

const InfoBox = styled.div`
  margin-bottom: 20px;

  .ant-alert-info {
    background-color: #f0f7ff;
    border: 1px solid #adc6ff;
    border-radius: 8px;
  }
  .ant-alert-message {
    font-weight: bold;
    color: #1d39c4;
  }
  .ant-alert-description {
    color: #262626;
    font-size: 14px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  label {
    font-weight: 600;
    color: #595959;
  }
`;

const Body = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const TreePanel = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
  min-height: 360px;
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;

  .anticon {
    color: #2563eb;
  }
`;

const StyledTree = styled(Tree)`
  .ant-tree-node-content-wrapper {
    padding: 2px 6px;
  }
  .ant-tree-treenode {
    padding: 2px 0;
  }
`;

// 선택 불가한 서브 카테고리 노드 라벨 (기본 커서 + 흐린 색)
const SubNodeLabel = styled.span`
  color: #94a3b8;
  cursor: default;
`;

const WidgetArea = styled.div`
  flex-shrink: 0;

  /* 위젯은 카테고리 페이지 기준 sticky 로 설계되어 있어, 관리자 페이지에서는
     상단 정렬 고정으로 덮어써 트리와 나란히 보이게 한다. */
  & > div {
    position: static;
    top: auto;
    max-height: none;
  }
`;

const PlaceHolder = styled.div`
  /* 3열 위젯 폭(236*3 + 10*2 + 32)과 맞춤 */
  width: 760px;
  max-width: 100%;
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;
